import React, { useEffect, useState } from 'react';
import { cn } from './model/derive';
import { SOUND_KEY, chime, loadSound, saveSound } from './ops/inboxAlarm';
import './SoundSettings.css';

/** Settings 의 알림음 카드 — 켜기·볼륨(1%)은 이 브라우저에만 저장한다 */

const SPEAKER = 'M3 9v6h4l5 4V5L7 9H3z';
const WAVES =
  'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z';

const Speaker = ({ loud = false, className }) => (
  <svg viewBox='0 0 24 24' aria-hidden className={className} fill='currentColor'>
    <path d={SPEAKER} />
    {loud && <path d={WAVES} />}
  </svg>
);

const Switch = ({ checked, onChange, label }) => (
  <button
    type='button'
    role='switch'
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={cn(
      'group relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors duration-200 ease-out motion-reduce:transition-none',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-deep02',
      checked ? 'bg-functional-success03' : 'bg-[rgba(120,120,128,0.16)]',
    )}
  >
    <span
      aria-hidden
      className={cn(
        'absolute left-[2px] top-[2px] h-[27px] w-[27px] rounded-full bg-grayscale-gray00',
        'shadow-[0_3px_8px_rgba(0,0,0,0.15),0_3px_1px_rgba(0,0,0,0.06)]',
        'transition-[transform,width] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none',
        'group-active:w-[33px]',
        checked ? 'translate-x-[20px] group-active:translate-x-[14px]' : 'translate-x-0',
      )}
    />
  </button>
);

const SoundSettings = ({ description }) => {
  const [sound, setSound] = useState(loadSound);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SOUND_KEY) setSound(loadSound());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const update = (patch) => {
    const next = { ...sound, ...patch };
    saveSound(next);
    setSound(next);
  };

  return (
    <section className='flex flex-col rounded-2xl bg-grayscale-gray00 p-6 shadow-4'>
      <h2 className='mb-2 text-[16px] font-medium text-text-primary'>Sound</h2>

      <div className='flex items-center gap-3 py-2'>
        <span className='flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-green-deep02 text-grayscale-gray00'>
          <Speaker loud className='h-[18px] w-[18px]' />
        </span>
        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <span className='text-[15px] leading-[140%] tracking-[-0.01em] text-text-primary'>
            Alert sound
          </span>
          <span className='text-[13px] leading-[145%] text-text-secondary'>{description}</span>
        </span>
        <Switch checked={sound.on} onChange={(on) => update({ on })} label='Alert sound' />
      </div>

      <div
        aria-hidden={!sound.on}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none',
          sound.on ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className='min-h-0 overflow-hidden'>
          <div className='ml-[42px] flex items-center gap-3 border-t border-[rgba(60,60,67,0.12)] py-3'>
            <Speaker className='h-4 w-4 shrink-0 text-text-tertiary' />
            <input
              type='range'
              min={0}
              max={100}
              step={1}
              value={sound.volume}
              disabled={!sound.on}
              aria-label='Volume'
              aria-valuetext={`${sound.volume}%`}
              onChange={(e) => update({ volume: Number(e.target.value) })}
              onPointerUp={chime}
              onKeyUp={(e) => e.key.startsWith('Arrow') && chime()}
              className='td-volume min-w-0 flex-1'
              style={{ '--pct': `${sound.volume}%` }}
            />
            <Speaker loud className='h-[18px] w-[18px] shrink-0 text-text-tertiary' />
            <span className='w-11 shrink-0 text-right text-[14px] text-text-secondary'>
              {sound.volume}%
            </span>
          </div>
        </div>
      </div>

      <p className='ml-[42px] mt-1 text-[12px] leading-[145%] text-text-tertiary'>
        {sound.on
          ? 'Saved on this browser. You’ll hear a preview when you let go of the slider.'
          : 'Alerts are silent on this browser.'}
      </p>
    </section>
  );
};

export default SoundSettings;
